import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";
import { CheckCircle2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import type { CrmDataProvider } from "../providers/types";
import type { DealExpedienteMissingEntry } from "../types";

/** Query key of the closing guard's read (one operation at a time). */
export const OPERATION_EXPEDIENTE_GUARD_KEY = "operation-expediente-guard";

/** Route contract shared with the contact file: the Expediente tab of a contact. */
export const expedienteTabPath = (contactId: Identifier) =>
  `/contacts/${contactId}/show?tab=expediente`;

export type OperationExpedienteGuardState = {
  status: "loading" | "error" | "ready";
  entries: DealExpedienteMissingEntry[];
  /** True only when the lead has contacts and every one of them is complete. */
  isClear: boolean;
  refetch: () => void;
};

/**
 * Almikia Epic EC2b (TASK-125): the missing expediente labels of every contact of an operation's
 * lead, read through `getOperationExpedienteMissing` (labels only, never a value; gated like
 * signing, so every role that can sign sees the list even when RLS hides the lead from it). The
 * SQL stays the authority; this only lets the sign dialog say what is missing BEFORE confirming.
 */
export const useOperationExpedienteGuard = (
  operationId: string | undefined,
  enabled: boolean,
): OperationExpedienteGuardState => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const query = useQuery({
    queryKey: [OPERATION_EXPEDIENTE_GUARD_KEY, operationId],
    queryFn: () =>
      dataProvider.getOperationExpedienteMissing(operationId as string),
    enabled: enabled && operationId != null,
    // The advisor completes the expediente in another tab and comes back: always re-read.
    staleTime: 0,
    gcTime: 0,
  });
  const entries = query.data ?? [];
  const status = query.isError
    ? "error"
    : query.isPending || query.isFetching
      ? "loading"
      : "ready";
  return {
    status,
    entries,
    isClear:
      status === "ready" &&
      entries.length > 0 &&
      entries.every((entry) => entry.missing.length === 0),
    refetch: () => {
      void query.refetch();
    },
  };
};

const GUARD_STATUS_STYLES = {
  missing: "bg-brand-red-bg text-brand-red-text",
  complete: "bg-brand-green-bg text-brand-green-text",
} as const;

/**
 * The incomplete contacts first (each with what is missing and a link to her Expediente tab), then
 * the complete ones. Semaforo: always icon plus text, never color alone.
 */
export const OperationExpedienteGuard = ({
  guard,
}: {
  guard: OperationExpedienteGuardState;
}) => {
  const translate = useTranslate();
  const { status, entries, refetch } = guard;

  if (status === "loading") {
    return (
      <div
        role="status"
        aria-busy="true"
        data-testid="expediente-guard-loading"
        className="space-y-2"
      >
        <span className="sr-only">
          {translate("crm.operations.expediente_guard.loading")}
        </span>
        <Skeleton aria-hidden="true" className="h-14 w-full" />
        <Skeleton aria-hidden="true" className="h-14 w-full" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        role="alert"
        className="space-y-2 rounded-md border border-brand-red-text bg-brand-red-bg p-3 text-[15px] text-brand-red-text"
      >
        <p>{translate("crm.operations.expediente_guard.error")}</p>
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          onClick={refetch}
        >
          {translate("crm.operations.expediente_guard.retry")}
        </Button>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p
        role="alert"
        className={`flex items-start gap-2 rounded-md p-3 text-[15px] ${GUARD_STATUS_STYLES.missing}`}
      >
        <XCircle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <span>{translate("crm.operations.expediente_guard.no_contact")}</span>
      </p>
    );
  }

  const ordered = [...entries].sort(
    (a, b) =>
      Number(b.missing.length > 0) - Number(a.missing.length > 0) ||
      Number(a.contact_id) - Number(b.contact_id),
  );

  return (
    <section
      aria-label={translate("crm.operations.expediente_guard.title")}
      className="space-y-2"
    >
      <h3 className="text-[15px] font-semibold">
        {translate("crm.operations.expediente_guard.title")}
      </h3>
      <ul className="space-y-2">
        {ordered.map((entry) => {
          const isMissing = entry.missing.length > 0;
          return (
            <li
              key={String(entry.contact_id)}
              data-testid={`expediente-guard-contact-${entry.contact_id}`}
              className={`space-y-1 rounded-md p-3 text-[15px] ${
                isMissing
                  ? GUARD_STATUS_STYLES.missing
                  : GUARD_STATUS_STYLES.complete
              }`}
            >
              <p className="flex items-start gap-2 font-medium">
                {isMissing ? (
                  <XCircle
                    className="mt-0.5 size-5 shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckCircle2
                    className="mt-0.5 size-5 shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>
                  {entry.contact_name}
                  {": "}
                  {isMissing
                    ? translate("crm.operations.expediente_guard.incomplete")
                    : translate("crm.operations.expediente_guard.complete")}
                </span>
              </p>
              {isMissing ? (
                <>
                  <p className="pl-7">
                    {translate("crm.operations.expediente_guard.missing")}{" "}
                    {entry.missing.join(", ")}
                  </p>
                  <Link
                    to={expedienteTabPath(entry.contact_id)}
                    className="ml-7 inline-flex min-h-11 items-center font-medium underline sm:min-h-9"
                  >
                    {translate("crm.operations.expediente_guard.open")}
                  </Link>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
